import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole, isSuperAdmin, canModerateForum } from "@/lib/accountRole";
import { openFeedback } from "@/components/FeedbackModal";
import AccountMenu from "@/components/AccountMenu";
import { useInboxUnread } from "@/context/InboxUnreadContext";
import { Plus, MessageSquare, MessageSquarePlus, SquarePen, ShieldAlert, ShieldCheck, X } from "lucide-react";

// ONE source of truth for the authed header's right side, used by BOTH the member header
// (Navbar.jsx) and the admin shell top bar (AdminShell.jsx) so they can never drift apart again.
//  - HeaderRightCluster : primary-row right cluster — BROWSE MARKETPLACE (filled olive) + ADMIN
//    (red outline, admins/super-admins only) + avatar (Sign out lives inside the avatar menu).
//  - HeaderActions      : the shared action set — + POST + feedback · messages · forum icons. Rendered
//    in the desktop second row (HeaderSecondaryRow) AND inlined into the mobile brand/hamburger row.
const ICON_BOX = "relative w-9 h-9 flex items-center justify-center border border-sniper text-secondary-foreground hover:text-foreground hover:border-olive/60 transition-colors";
const ICON_BADGE = "absolute -top-1.5 -right-1.5 bg-blaze text-black text-[9px] font-bold px-1 min-w-[16px] text-center leading-4";

export function HeaderRightCluster() {
  const { user, adminMode, setAdminMode } = useAuth();
  const navigate = useNavigate();
  const admin = isAdminRole(user?.role);
  if (!user) return null;
  const inAdminMode = admin && adminMode === "admin";
  const superAdmin = isSuperAdmin(user?.role);
  const modeLabel = superAdmin ? "Super Admin Mode" : "Admin Mode";
  // Chip colour matches the mode band so the two agree: super stays orange, admin is red.
  const chipBg = superAdmin ? "#C2600F" : "#A33D28";
  const exitBg = superAdmin ? "#9c4d0c" : "#7f2f1f";
  return (
    <div className="flex items-center gap-1.5" data-testid="header-right-cluster">
      {admin && !inAdminMode && (
        // USER MODE: role is present but not activated — a plain entry into the portal.
        <button
          onClick={() => { setAdminMode("admin"); navigate("/admin"); }}
          data-testid="nav-admin"
          title="Enter admin mode"
          className="flex items-center gap-1 px-2 py-1.5 border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-mono uppercase text-[11px] tracking-wide transition-colors whitespace-nowrap"
        >
          <ShieldAlert className="w-4 h-4" strokeWidth={2} /> Admin
        </button>
      )}
      {inAdminMode && (
        // ADMIN MODE: the lit chip. Role is distinguished HERE in the label only (amber for both).
        <div className="flex items-center" data-testid="admin-mode-chip">
          <button
            onClick={() => navigate("/admin")}
            data-testid="nav-admin"
            title="Admin portal"
            className="flex items-center gap-1 px-2 py-1.5 text-white font-mono uppercase text-[11px] tracking-wide whitespace-nowrap hover:brightness-110 transition-all"
            style={{ backgroundColor: chipBg }}
          >
            <ShieldAlert className="w-4 h-4" strokeWidth={2} /> {modeLabel}
          </button>
          <button
            onClick={() => { setAdminMode("user"); navigate("/feed"); }}
            data-testid="admin-mode-exit"
            title="Exit admin mode"
            className="flex items-center px-1.5 py-1.5 text-white hover:brightness-90 transition-all"
            style={{ backgroundColor: exitBg }}
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      )}
      <div className="ml-0.5"><AccountMenu /></div>
    </div>
  );
}

// Shared authed action set: + POST plus the icon buttons. `compact` shrinks it a touch so it fits
// on one line inside the mobile brand/hamburger row. Icons: feedback · messages (unread badge) · forum.
export function HeaderActions({ compact = false }) {
  const { user, adminMode } = useAuth();
  const { pathname, search } = useLocation();
  const { total: unread } = useInboxUnread();
  if (!user) return null;
  const from = `${pathname}${search || ""}`; // return the composer to where you were
  const inForum = pathname.startsWith("/forum");
  // In admin mode the messages icon opens TEAM chat (staff channels / DMs), not seller threads.
  const inAdminMode = isAdminRole(user?.role) && adminMode === "admin";
  const messagesTo = inAdminMode ? "/inbox?view=team" : "/inbox";
  const messagesTitle = inAdminMode ? "Team chat" : "Messages";
  // + POST is a marketplace-contextual action — render it ONLY on the Marketplace page.
  const onMarketplace = pathname === "/feed";
  const box = compact
    ? "relative w-8 h-8 flex items-center justify-center border border-sniper text-secondary-foreground hover:text-foreground hover:border-olive/60 transition-colors"
    : ICON_BOX;
  const iconCls = compact ? "w-[18px] h-[18px]" : "w-5 h-5";
  const postCls = compact
    ? "inline-flex items-center gap-1 h-8 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-[11px] px-2.5 transition-colors whitespace-nowrap"
    : "inline-flex items-center gap-1.5 h-9 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-[12px] px-3 transition-colors whitespace-nowrap";
  return (
    <div className={`flex items-center ${compact ? "gap-1.5" : "gap-2"}`} data-testid="header-actions">
      {onMarketplace && (
        <Link to="/create" state={{ from }} data-testid="nav-create" className={postCls}>
          <Plus className="w-4 h-4" strokeWidth={2.5} /> Post
        </Link>
      )}
      <button onClick={openFeedback} data-testid="nav-feedback" title="Give beta feedback" className={box}>
        <MessageSquarePlus className={iconCls} strokeWidth={1.6} />
      </button>
      <Link to={messagesTo} data-testid="nav-messages" title={messagesTitle} className={box}>
        <MessageSquare className={iconCls} strokeWidth={1.6} />
        {unread > 0 && <span className={ICON_BADGE} data-testid="nav-messages-badge">{unread}</span>}
      </Link>
      {compact && !inForum && (
        <Link to="/forum" data-testid="nav-forum-icon" title="Forum" className={box}>
          <SquarePen className={iconCls} strokeWidth={1.6} />
        </Link>
      )}
      {canModerateForum(user) && (
        <Link to="/mod" data-testid="nav-mod" title="Forum moderation" className={box}>
          <ShieldCheck className={iconCls} strokeWidth={1.6} />
        </Link>
      )}
    </div>
  );
}

// Desktop second row — the shared action set, right-aligned. On the member header this is rendered
// md-and-up only (mobile merges the actions up into the brand/hamburger row); the admin shell renders
// it on all sizes below its own top bar.
export function HeaderSecondaryRow() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="border-b border-sniper" data-testid="header-secondary-row">
      <div className="max-w-[1400px] mx-auto px-4 h-11 flex items-center justify-end">
        <HeaderActions />
      </div>
    </div>
  );
}
